/**
 * Queries and mutations for the Teams area (`/teams/*`), admins and team managers.
 *
 * Its own key root. Every write answers with the team as Ivanti now holds it,
 * which is written straight into the detail cache; the list is invalidated so
 * its member counts and problem badges follow. Handing a team to a new
 * manager also re-reads `/auth/me`, because it can end the caller's access.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addTeamMember as sdkAddTeamMember,
  getTeam as sdkGetTeam,
  listTeams as sdkListTeams,
  removeTeamMember as sdkRemoveTeamMember,
  type TeamDetailResponse,
  type TeamListResponse,
  type TeamUpdateRequest,
  updateTeam as sdkUpdateTeam,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { countRefetchInterval } from "./lib/team-count";

export const teamKeys = {
  all: ["teams"] as const,
  list: () => [...teamKeys.all, "list"] as const,
  detail: (teamId: string) => [...teamKeys.all, "detail", teamId] as const,
};

/** The teams this person may see: every team for an admin, their own for a manager. */
export function useTeams() {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: async (): Promise<TeamListResponse> => {
      const { data } = await sdkListTeams({ throwOnError: true });
      return data;
    },
    // A 403 is an answer, not a blip: retrying it only delays the message.
    retry: false,
    // A count-only team's count arrives in the background (2 Oct): ask again
    // until it has, then stop.
    refetchInterval: (query) => countRefetchInterval(query.state.data?.items),
  });
}

/** One team with members and problems. */
export function useTeam(teamId: string) {
  return useQuery({
    queryKey: teamKeys.detail(teamId),
    queryFn: async (): Promise<TeamDetailResponse> => {
      const { data } = await sdkGetTeam({ path: { team_id: teamId }, throwOnError: true });
      return data;
    },
    retry: false,
    refetchInterval: (query) =>
      countRefetchInterval(query.state.data ? [query.state.data] : undefined),
  });
}

/**
 * After a member change that failed, show the team as Ivanti holds it now.
 *
 * A failure may still have changed something (19 Oct: Ivanti can answer OK
 * and not hold the change, or the other way round), so the page never keeps
 * showing what it assumed.
 */
function useRefreshTeam(teamId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    void queryClient.invalidateQueries({ queryKey: teamKeys.list() });
  };
}

/** Store a write's answer and refresh everything it can have changed. */
function useApplyTeamChange() {
  const queryClient = useQueryClient();
  return (team: TeamDetailResponse, { managerChanged = false } = {}) => {
    queryClient.setQueryData(teamKeys.detail(team.id), team);
    void queryClient.invalidateQueries({ queryKey: teamKeys.list() });
    if (managerChanged) {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    }
  };
}

/** Change the team email and/or the manager. Omitted fields stay as they are. */
export function useUpdateTeam(teamId: string) {
  const apply = useApplyTeamChange();
  return useMutation({
    mutationFn: async (body: TeamUpdateRequest): Promise<TeamDetailResponse> => {
      logger.debug("[teams] update", teamId, Object.keys(body));
      const { data } = await sdkUpdateTeam({
        path: { team_id: teamId },
        body,
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    onSuccess: (team, body) => apply(team, { managerChanged: body.manager_id != null }),
  });
}

/**
 * Add an employee (by directory RecId) to the team. The server answers only
 * once the team read back from Ivanti lists them (19 Oct).
 */
export function useAddTeamMember(teamId: string) {
  const apply = useApplyTeamChange();
  const refresh = useRefreshTeam(teamId);
  return useMutation({
    mutationFn: async (employeeId: string): Promise<TeamDetailResponse> => {
      logger.debug("[teams] add member", teamId, employeeId);
      const { data } = await sdkAddTeamMember({
        path: { team_id: teamId },
        body: { employee_id: employeeId },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    onSuccess: (team) => apply(team),
    onError: refresh,
  });
}

/**
 * Remove a member from the team; their employee record is untouched. The
 * server answers only once the team read back from Ivanti no longer lists them.
 */
export function useRemoveTeamMember(teamId: string) {
  const apply = useApplyTeamChange();
  const refresh = useRefreshTeam(teamId);
  return useMutation({
    mutationFn: async (employeeId: string): Promise<TeamDetailResponse> => {
      logger.debug("[teams] remove member", teamId, employeeId);
      const { data } = await sdkRemoveTeamMember({
        path: { team_id: teamId, employee_id: employeeId },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    onSuccess: (team) => apply(team),
    onError: refresh,
  });
}
